import { useCallback, useMemo } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Add, Refresh } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Divider, Stack } from '@mui/material';
import styled from '@emotion/styled';
import { FaTrash } from 'react-icons/fa6';

import useTranslation from 'Hooks/useTranslation';
import GenericTable from 'Pages/reports/[submodules]/employees/components/GenericTable/GenericTable';
import useEmployeeDevelopmentPlan from './Hooks/useEmployeeDevelopmentPlan';
import CourseStatusChip from 'Components/CourseStatusChip';
import CoursePriorityTag from 'Components/CoursePriorityTag/CoursePriorityTag';
import ConditionalRender from 'Components/ConditionalRender';
import { COURSE_STATUS_APPROVED } from 'Constants/General';
import { colors } from 'Constants/Colors';
import { usePermissionStore } from 'stores';

import ActionsButton from './Components/ActionsButton';
import BulkApproveModal from './Components/BulkApproveModal';
import CourseAssignModal from './Components/CourseAssignModal';
import DeassignConfirmationModal from './Components/DeassignConfirmationModal';
import ResetProgressModal from './Components/ResetProgressModal';
import Title from './Components/Title';

import container, { ContainerTitle } from './style';

const CustomButton = styled(Button)`
  background-color: ${colors.GREEN};
  color: ${colors.WHITE};
  font-weight: bold;
  &:hover {
    background-color: ${colors.GREEN_LEAF};
  }
`;

const hasPermission = (permissions = [], lookup = '') => {
  return !!permissions.find(it => it.key === lookup && it.assigned === true);
};

export default function Employee({ employee, isAdmin }) {
  const { t2 } = useTranslation('EmployeeAssess');
  const permissions = usePermissionStore(state => state.permissions);

  const {
    openApprove,
    toggleApprove,
    selectedCourse,
    developmentPlan,
    approveUserCourses,
    updateUserCourse,
    openResetProgress,
    setSelectedCourse,
    toggleResetProgress,
    openCourseAssign,
    toggleCourseAssigner,
    assignCourses,
    courses,
    openDeassignConfirmation,
    toggleDeassignConfirmation,
  } = useEmployeeDevelopmentPlan({
    userId: employee[employee.length - 2],
  });

  const userId = employee[employee.length - 2];

  const actionOptions = useCallback(
    (canApprove, course, canDelete = false) => {
      const options = [];

      if (canDelete) {
        options.push({
          label: 'Desasignar curso',
          icon: <FaTrash sx={{ height: 17, width: 17, color: colors.RED }} />,
          onClick: () => {
            setSelectedCourse(course);
            toggleDeassignConfirmation(true);
          },
        });
      }

      if (canApprove) {
        options.push({
          label: t2('approve'),
          icon: <EditIcon sx={{ height: 17, width: 17, color: colors.BLACK }} />,
          onClick: () => {
            setSelectedCourse(course);
            toggleApprove(true);
          },
        });
      }

      options.push({
        label: t2('reset_progress'),
        icon: <Refresh sx={{ height: 17, width: 17, color: colors.BLACK }} />,
        onClick: () => {
          setSelectedCourse(course);
          toggleResetProgress(true);
        },
      });

      return options;
    },
    [setSelectedCourse, toggleDeassignConfirmation, toggleApprove, toggleResetProgress, t2]
  );

  const columns = useMemo(() => {
    const draft = [
      { id: 'name', label: t2('course_name') },
      { id: 'duration', label: t2('time') },
      { id: 'priority', label: t2('priority') },
      { id: 'status', label: t2('status'), align: 'center' },
    ];

    if (hasPermission(permissions, 'employee_development.*') || hasPermission(permissions, 'user_course.*')) {
      draft.push({ id: 'actions', label: t2('actions'), align: 'center' });
    }

    return draft;
  }, [t2, permissions]);

  const rows = useCallback(
    data => {
      if (!data) return [];

      return data.map(course => {
        const approve = hasPermission(permissions, 'employee_development.*');
        let canApprove = false;

        if (course.UserCourseStatus.id !== COURSE_STATUS_APPROVED) {
          canApprove = approve;
        }

        return {
          name: course.name,
          duration: course.duration ?? t2('not_available'),
          priority: <CoursePriorityTag id={course.Course.Priority.id} name={course.Course.Priority.name} />,
          status: <CourseStatusChip statusId={course.UserCourseStatus.id} status={course.UserCourseStatus.name} />,
          actions: approve && (
            <ActionsButton
              label={<MoreHorizIcon sx={{ color: colors.BLACK }} />}
              options={actionOptions(canApprove, course)}
            />
          ),
        };
      });
    },
    [t2, isAdmin, developmentPlan, actionOptions, permissions]
  );

  const rowBuilder = useCallback(() => {
    if (!courses) return [];

    return courses.map(it => {
      const desassign = hasPermission(permissions, 'user_course.*');
      const approver = hasPermission(permissions, 'employee_development.*');

      let canApprove = false;
      if (it.UserCourseStatus.id !== COURSE_STATUS_APPROVED) {
        canApprove = approver;
      }

      return {
        name: it.name,
        duration: it.duration ?? t2('not_available'),
        priority: <CoursePriorityTag id={it.Course.Priority.id} name={it.Course.Priority.name} />,
        status: <CourseStatusChip statusId={it.UserCourseStatus.id} status={it.UserCourseStatus.name} />,
        actions: (
          <ActionsButton
            label={<MoreHorizIcon sx={{ color: colors.BLACK }} />}
            options={actionOptions(canApprove, it, desassign)}
          />
        ),
      };
    });
  }, [courses, actionOptions, permissions, t2]);

  return (
    <div style={container} className="col">
      <Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Title title={`${t2('DevelopmentPlan')} de`} employee={employee[1]} />
          <ConditionalRender condition={hasPermission(permissions, 'user_course.*')}>
            <CustomButton startIcon={<Add />} onClick={() => toggleCourseAssigner(true)}>
              Asignar cursos
            </CustomButton>
          </ConditionalRender>
        </Stack>
        <Divider sx={{ marginBottom: '16px' }} />
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ContainerTitle>{t2('CoursesByEnterprise')}</ContainerTitle>
        <GenericTable columns={columns} rows={rows(developmentPlan?.enterprise || [])} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ContainerTitle>{t2('CoursesByDepartment')}</ContainerTitle>
        <GenericTable columns={columns} rows={rows(developmentPlan?.department || [])} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ContainerTitle>{t2('CoursesByJobTitle')}</ContainerTitle>
        <GenericTable columns={columns} rows={rows(developmentPlan?.jobTitle || [])} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ContainerTitle>Cursos asignados</ContainerTitle>
        <GenericTable columns={columns} rows={rowBuilder(courses || [])} />
      </Box>

      <BulkApproveModal
        open={openApprove}
        course={selectedCourse}
        onClose={() => toggleApprove(false)}
        onApprove={approveUserCourses}
      />

      <CourseAssignModal
        visible={openCourseAssign}
        userId={userId}
        onAssign={assignCourses}
        onClose={() => toggleCourseAssigner(false)}
      />

      <ResetProgressModal
        open={openResetProgress}
        onAccept={() => updateUserCourse('reset_progress')}
        onClose={() => toggleResetProgress(false)}
        onCancel={() => toggleResetProgress(false)}
      />

      <DeassignConfirmationModal
        open={openDeassignConfirmation}
        onAccept={() => updateUserCourse('deassign')}
        onClose={() => toggleDeassignConfirmation(false)}
        onCancel={() => toggleDeassignConfirmation(false)}
      />
    </div>
  );
}
